import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { range, uniq } from 'lodash-es';
import { selectGreenhouse } from 'state';
import { Map } from 'components/Map';
import { Select } from 'components/_shared/Select';
import { varietyRequests } from 'api';
import { getFirstDateOfYear, getISOWeekDateRange } from './helpers';
import 'react-datepicker/dist/react-datepicker.css';

import {
  Container,
  ChartsContainer,
  Filters,
  FilterButton,
  ButtonWrapper,
  HelpText,
  SelectOption
} from './CanopyDetection.styles';

const START_YEAR = new Date().getFullYear();

export const CanopyDetection = () => {
  const { selectedGreenhouse } = useSelector(selectGreenhouse);
  const [selectedVariety, setSelectedVariety] = useState();
  const [filterState, setFilterState] = useState({ year: START_YEAR });
  const [locations, setLocations] = useState([]);
  const [chart, setChart] = useState([]);
  const [filteredChart, setFilteredChart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [parameter, setParameter] = useState('canopy_percentage');
  const [yearsRange, setYearsRange] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);

  const years = selectedLocation => {
    const location_id = selectedLocation.id ? selectedLocation.id : selectedLocation;
    const location = locations.find(loc => loc.id === location_id);
    if (!location) return [];
    const start = new Date(location.start_date);
    const end = location.end_date ? new Date(location.end_date) : new Date();

    if (isNaN(start)) return [];

    const startYear = start.getFullYear();
    const endYear = isNaN(end) ? new Date().getFullYear() : end.getFullYear();

    return range(startYear, endYear + 1);
  };

  useEffect(() => {
    const week = weekSelectItems(filterState.year, chart).at(-1);
    if (week === undefined || !firstLoad) return;
    setFilterState({
      ...filterState,
      week: week.value
    });
    const { start, end } = getISOWeekDateRange(week.value, filterState.year);
    getChartData(selectedGreenhouse, start, end);
    setFirstLoad(false);
  }, [chart]);

  useEffect(() => {
    varietyRequests.getVarietyMap(selectedGreenhouse).then(({ data }) => {
      setLocations(data);
      const variety = data.at(-1);
      setSelectedVariety(variety);
      setFilterState({
        ...filterState,
        year: START_YEAR
      });
      setYearsRange(years(variety));
      const firstWeek = getISOWeekDateRange(1, START_YEAR);
      getChartData(
        selectedGreenhouse,
        firstWeek.start,
        `${START_YEAR}-12-31`,
        setChart,
        variety
      );
    });
  }, [selectedGreenhouse]);

  const dateRange = () => {
    return getISOWeekDateRange(filterState.week, filterState.year);
  };

  const varietys = useMemo(
    () =>
      uniq(
        chart
          .filter(x => 'variety_name' in x.properties)
          .map(x => x.properties.variety_name)
      ),
    [chart]
  );
  const missions_by_planting_date = useMemo(
    () =>
      filteredChart.filter(mission => {
        const planting_date =
          !filterState.planting_date ||
          filterState.planting_date === mission.properties.planting_date;
        const variety =
          !filterState.variety || filterState.variety === mission.properties.variety_name;
        return planting_date && variety;
      }),
    [filterState.planting_date, filterState.variety, filteredChart]
  );

  // dates for weeks
  const missionDates = chart => {
    return uniq(chart.map(x => x.properties.mission_date.split(' ')[0]));
  };

  const plantingDates = useMemo(
    () =>
      uniq(
        chart
          .filter(x => 'planting_date' in x.properties)
          .map(x => x.properties.planting_date.split(' ')[0])
      ).sort((a, b) => {
        return new Date(a) - new Date(b);
      }),
    [chart]
  );

  const weekSelectItems = (selectedYear, chart) => {
    const dates = missionDates(chart);
    if (!dates.length) return [];

    function mapCB(_, i) {
      const { start, end } = getISOWeekDateRange(i + 1, selectedYear);
      const hasData = dates.some(x => {
        const startDate = new Date(start);
        const currentDate = new Date(x);
        const endDate = new Date(end);
        return startDate <= currentDate && currentDate <= endDate;
      });
      const option = `${i + 1} week (${start} - ${end})`;
      return {
        name: <SelectOption isActive={hasData}>{option}</SelectOption>,
        value: i + 1,
        hasData: hasData,
        option
      };
    }

    return [...Array(52)].map(mapCB).filter(item => item.hasData === true);
  };

  const getChartData = (greenhouse, startDate, endDate, cb, defaultVariety) => {
    setFilteredChart([]); // clear data to rerender map
    setLoading(true);
    const variety = defaultVariety
      ? defaultVariety.id
      : selectedVariety.id
      ? selectedVariety.id
      : selectedVariety;
    varietyRequests
      .getVarietyChart(greenhouse, startDate, endDate)
      .then(({ data }) => {
        const filtered = data.filter(d => d.properties?.map_id === variety);
        cb?.(filtered);
        setFilteredChart(filtered);
      })
      .finally(() => setLoading(false));
  };

  const handleApplyFilter = () => {
    setLoading(true);
    const { week, year } = filterState;
    if (week === undefined || year === undefined) {
      setLoading(false);
      return;
    }
    const { start, end } = getISOWeekDateRange(week, year);

    const startDate = start || getFirstDateOfYear(year);
    const endDate = end || dateRange?.end;

    getChartData(selectedGreenhouse, startDate, endDate);
  };

  const handleClearFilter = () => {
    setFilterState({});
    setChart([]);
    setFilteredChart([]);
    setSelectedVariety(null);
  };

  const handleLocationChange = async ({ value }) => {
    setSelectedVariety(value);
    setYearsRange(years(value));
    setFilterState({});
  };

  const handleYearChange = ({ value }) => {
    if (value === filterState.year) return;
    const firstWeek = getISOWeekDateRange(1, value);
    setFilterState({
      ...filterState,
      year: value,
      day: '',
      week: '',
      mission: ''
    });
    getChartData(selectedGreenhouse, firstWeek.start, `${value}-12-31`, setChart);
  };

  const handleWeekChange = ({ value }) => {
    setFilterState({ ...filterState, week: value });
    const { start, end } = getISOWeekDateRange(value, filterState.year);
    getChartData(selectedGreenhouse, start, end);
  };

  const handleParameterChange = ({ value }) => {
    setParameter(value);
    handleApplyFilter();
  };

  return (
    <Container>
      <Filters>
        <Select
          items={locations.map(l => ({ name: l.name, value: l.id }))}
          onSelect={handleLocationChange}
          value={selectedVariety}
          placeholder={selectedVariety ? selectedVariety.name : 'Select variety map'}
          label='Select variety map'
        />
        {selectedVariety && (
          <>
            <Select
              items={
                yearsRange.length
                  ? yearsRange.map(v => ({ name: v, value: v }))
                  : years(selectedVariety).map(v => ({ name: v, value: v }))
              }
              onSelect={handleYearChange}
              value={filterState.year}
              placeholder='All Years'
              label='Year'
            />
            {filterState.year && chart.length !== 0 && (
              <>
                <Select
                  items={weekSelectItems(filterState.year, chart)}
                  onSelect={handleWeekChange}
                  value={filterState.week}
                  placeholder='Select Week'
                  label='Week'
                />
                <Select
                  items={[
                    { name: 'All Varieties', value: null },
                    ...varietys.map(v => ({ name: v, value: v }))
                  ]}
                  onSelect={({ value }) =>
                    setFilterState({ ...filterState, variety: value })
                  }
                  value={filterState.variety}
                  placeholder='Select Variety'
                  label='Variety'
                />
              </>
            )}
            <Select
              items={[
                { name: 'All dates', value: null },
                ...plantingDates
                  .filter(v => v !== 'NaT')
                  .map(v => ({ name: v, value: v }))
              ]}
              onSelect={({ value }) =>
                setFilterState({ ...filterState, planting_date: value })
              }
              value={filterState.planting_date}
              placeholder='All dates'
              label='Planting Date'
            />
            <Select
              items={[
                { name: 'Canopy Coverage', value: 'canopy_percentage' },
                { name: 'Induction per m²', value: 'induction_per_m2' },
                { name: 'Yellow Leaves per m²', value: 'yellow_leaves_per_m2' },
                { name: 'Brown Leaves per m²', value: 'brown_leaves_per_m2' }
              ]}
              onSelect={handleParameterChange}
              value={parameter}
              label='Parameter'
            />
          </>
        )}
        <ButtonWrapper>
          <FilterButton
            fullWidth
            variant='secondary'
            variantType='danger'
            icon='Delete'
            onClick={handleClearFilter}
            disabled={!selectedVariety || loading}
            title={!selectedVariety ? 'Select variety' : ''}
          >
            Clear filter
          </FilterButton>
        </ButtonWrapper>
      </Filters>
      {filteredChart.length > 0 && missions_by_planting_date.length > 0 && (
        <ChartsContainer>
          <Map
            data={
              filterState.planting_date || filterState.variety
                ? missions_by_planting_date
                : filteredChart
            }
            backgroundData={
              locations.find(
                ({ id }) =>
                  id === (selectedVariety.id ? selectedVariety.id : selectedVariety)
              )?.map
            }
            fullData={chart}
            mapField={parameter}
          />
        </ChartsContainer>
      )}
      {(!selectedVariety ||
        filteredChart.length === 0 ||
        missions_by_planting_date.length === 0) && (
        <HelpText>{loading ? 'Loading...' : 'Set valid filter'}</HelpText>
      )}
    </Container>
  );
};
